'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { authService } from '@/lib/authService';
import { buildApiUrl, isAppShellRoute } from '@/lib/platformUtils';

/**
 * A client where the user has total-pabx and/or total-connect module access.
 * Returned by GET /api/v1/operations/accessible-clients.
 */
export interface AccessiblePabxClient {
  client_id: number;
  client_name: string;
  is_active_client: boolean;
  has_total_pabx: boolean;
  has_total_connect: boolean;
  pabx_role: string | null;
  connect_role: string | null;
  sip_extension: {
    extension_number: string;
    display_name: string;
    can_make_outbound_calls: boolean;
  } | null;
}

export type CallModalMode = 'pabx-only' | 'connect-only' | 'full' | 'none';

const STORAGE_KEY = 'totalconnect_selected_client_id';

/**
 * Hook that fetches all clients where the authenticated user has total-pabx
 * and/or total-connect module access, and manages the "selected client" —
 * the client context used for outbound calling, SIP extensions, parking, etc.
 *
 * The selected client is persisted to localStorage so it survives page
 * reloads. When the active client changes (client switch), if no explicit
 * selection was made the active client becomes the selected one.
 *
 * This decouples TotalConnectRTC from the JWT active client so a user with
 * PABX access across multiple clients can place calls and view history for
 * any of them without switching clients in the main app.
 */
export function useAccessiblePabxClients() {
  const [clients, setClients] = useState<AccessiblePabxClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const fetchedRef = useRef(false);

  const fetchClients = useCallback(async (force = false) => {
    if (!force && fetchedRef.current) return;
    try {
      setLoading(true);
      setError(null);
      // PABX client data is app-shell only — skip on public pages.
      if (typeof window !== 'undefined' && !isAppShellRoute(window.location.pathname)) {
        setLoading(false);
        return;
      }
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        setLoading(false);
        return;
      }

      const res = await fetch(buildApiUrl('/api/v1/operations/accessible-clients'), {
        headers: { 'Authorization': `Bearer ${tokens.access_token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch accessible clients');
      const data = await res.json();
      if (data.success && Array.isArray(data.clients)) {
        setClients(data.clients);
        // Initialize selected client:
        // 1. Check localStorage for a previously selected client
        // 2. Validate it's still in the accessible list
        // 3. Fall back to the active client
        const activeClient = data.clients.find((c: AccessiblePabxClient) => c.is_active_client);
        let storedId: number | null = null;
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (raw) {
            const parsed = Number.parseInt(raw, 10);
            if (Number.isFinite(parsed) && parsed > 0) {
              storedId = parsed;
            }
          }
        } catch {
          // ignore
        }

        const storedValid = storedId != null && data.clients.some((c: AccessiblePabxClient) => c.client_id === storedId);
        if (storedValid) {
          setSelectedClientId(storedId);
        } else if (activeClient) {
          setSelectedClientId(activeClient.client_id);
        } else if (data.clients.length > 0) {
          setSelectedClientId(data.clients[0].client_id);
        } else {
          setSelectedClientId(null);
        }
      }
    } catch (err: unknown) {
      console.error('[useAccessiblePabxClients] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load accessible clients');
    } finally {
      setLoading(false);
      fetchedRef.current = true;
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Persist selection to localStorage
  const selectClient = useCallback((clientId: number | null) => {
    setSelectedClientId(clientId);
    try {
      if (clientId != null) {
        localStorage.setItem(STORAGE_KEY, String(clientId));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // ignore
    }
  }, []);

  const selectedClient = useMemo(
    () => clients.find((c) => c.client_id === selectedClientId) ?? null,
    [clients, selectedClientId],
  );

  const hasMultipleClients = clients.length > 1;

  // Compute call modal mode for the selected client
  const callModalMode: CallModalMode = useMemo(() => {
    if (!selectedClient) return 'none';
    if (selectedClient.has_total_pabx && selectedClient.has_total_connect) return 'full';
    if (selectedClient.has_total_pabx) return 'pabx-only';
    if (selectedClient.has_total_connect) return 'connect-only';
    return 'none';
  }, [selectedClient]);

  return {
    clients,
    loading,
    error,
    selectedClientId,
    selectedClient,
    selectClient,
    hasMultipleClients,
    callModalMode,
    refresh: () => fetchClients(true),
  };
}

export default useAccessiblePabxClients;
